<template>
    <CqModal
        :busy="loading"
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Medewerkervolgordes per datum wijzigen"
    >
        <template v-if="!loading">
            <div v-for="(rosterRankSet, index) in rosterRankSets" :key="index" class="row">
                <div class="dateRange">
                    <span class="from">{{ rosterRankSet.beginDate.getTime() <= 0 ? 'Startdatum rooster' : formatDate(rosterRankSet.beginDate) }}</span>
                    <span class="until"> t/m {{ formatDate(rosterRankSet.endDate) ?? 'einddatum rooster' }}</span>
                </div>
            </div>
        </template>
        <div class="error" v-if="error">{{ error }}</div>
        <CqButtonBar>
            <CqButton @click="closeModal" label="Sluiten" :disabled="isSaving" />
            <CqButton @click="edit" label="Bewerk laatste" :disabled="isSaving || !rosterRankSets || rosterRankSets.length <= 1" />
            <CqButton @click="destroy" label="Verwijder laatste" :disabled="isSaving || !rosterRankSets || rosterRankSets.length <= 1" dangerous />
            <CqButton @click="add" label="Nieuw toevoegen" :disabled="isSaving || !rosterRankSets" primary />
        </CqButtonBar>
        <RosterRankSetEditModal
            :rosterRankSet="rosterRankSetToEdit"
            :minDate="minDate"
            :isSaving="isSaving"
            @saveRosterRankSet="saveRosterRankSet"
            @closeModal="closeEditModal"
        />
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal} from '@cquential/ui';
    import RosterRankSetEditModal from '@/components/modals/RosterRankSetEditModal.vue';
    import {RosterData} from '@/types/types';
    import GenericConfirmationModal from '@/components/modals/GenericConfirmationModal.vue';
    import {ApiClient, RosterRankSet} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {Component, Vue, Prop} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            RosterRankSetEditModal,
        },
        emits: [
            'update:rosterRankSets',
        ],
    })
    export default class RosterRankSetsModal extends Vue {
        private static instance: RosterRankSetsModal | null = null;

        @Prop() public roster?: RosterData;

        private error: string = '';
        private show: boolean = false;
        private isSaving: boolean = false;
        private minDate: Date | undefined = undefined;
        private rosterRankSetToEdit: RosterRankSet | null = null;
        private rosterRankSets: RosterRankSet[] | null = null;

        public mounted(): void {
            RosterRankSetsModal.instance = this;
        }

        public beforeUnmount(): void {
            RosterRankSetsModal.instance = null;
        }

        public open(): void {
            this.loadRosterRankSets();
            this.show = true;
        }

        public loadRosterRankSets(): void {
            ApiClient.instance.fetchRosterRankSets(this.roster!.rosterId!)
                .then((loadedRosterRankSets: RosterRankSet[]): void => {
                    this.rosterRankSets = loadedRosterRankSets.sort((a: RosterRankSet, b: RosterRankSet): number => a.beginDate.getTime() - b.beginDate.getTime());
                })
                .catch((): void => {
                    this.error = 'Kon de medewerkervolgordes per datum niet laden. Probeer het opnieuw.';
                });
        }

        public get lastRosterRankSet(): RosterRankSet {
            return this.rosterRankSets![this.rosterRankSets!.length - 1];
        }

        public get loading(): boolean {
            return !this.roster || !this.rosterRankSets;
        }

        public destroy(): void {
            if (this.isSaving) {
                return;
            }

            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Medewerkervolgorde verwijderen',
                `Wil je de medewerkervolgorde per ${Dates.Formatter.formatDateLong(this.lastRosterRankSet.beginDate)} verwijderen? Deze actie kan niet ongedaan worden gemaakt`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                if (this.lastRosterRankSet.id !== undefined) {
                    ApiClient.instance.deleteRosterRankSet(this.lastRosterRankSet)
                        .then((): void => {
                            this.loadRosterRankSets();
                        })
                        .catch((): void => {
                            this.error = 'Kon de laatste medewerkervolgorde per datum niet verwijderen. Probeer het opnieuw.';
                        });
                } else {
                    this.rosterRankSets = this.rosterRankSets?.filter((rankSet: RosterRankSet): boolean => rankSet.id !== this.lastRosterRankSet.id) ?? [];
                }
            });
        }

        public add(): void {
            if (this.rosterRankSets && this.rosterRankSets.length >= 2) {
                this.minDate = Dates.Utils.addDays(this.lastRosterRankSet.beginDate, 1);
            }

            this.rosterRankSetToEdit = {
                rosterId: this.rosterRankSets![0].rosterId,
                beginDate: this.minDate ?? new Date(),
                endDate: undefined,
            };
        }

        public edit(): void {
            const previousRosterRankSetIndex: number = this.rosterRankSets!.length - 2;
            if (this.rosterRankSets![previousRosterRankSetIndex]) {
                this.minDate = Dates.Utils.addDays(this.rosterRankSets![previousRosterRankSetIndex].beginDate!, 1);
            }

            this.rosterRankSetToEdit = {...this.lastRosterRankSet};
        }

        public saveRosterRankSet(rosterRankSet: RosterRankSet): void {
            if (this.isSaving) {
                return;
            }
            this.isSaving = true;

            ApiClient.instance.saveRosterRankSet(rosterRankSet)
                .then((): void => {
                    this.loadRosterRankSets();
                    this.$emit('update:rosterRankSets');
                })
                .catch((): void => {
                    this.error = 'Kon de medewerkervolgorde per datum niet opslaan. Probeer het opnieuw.';
                })
                .finally((): void => {
                    this.isSaving = false;
                });

            this.closeEditModal();
        }

        public closeModal(): void {
            this.show = false;
            this.minDate = undefined;
        }

        public closeEditModal(): void {
            this.rosterRankSetToEdit = null;
        }

        public formatDate(date?: Date): string | undefined {
            return date ? Dates.Formatter.formatDateHumanFriendly(date) : undefined;
        }

        public static open(): void {
            this.instance!.open();
        }
    }
</script>

<style lang="scss" scoped>
    .row {
        padding: 0.5rem;
        display: flex;
        justify-content: space-between;
        border-top: solid 1px var(--header-bar-text-color);

        &:first-child {
            border-top: none;
        }

        .dateRange {
            margin-top: 0.25rem;

            .until {
                opacity: 0.5;
            }
        }
    }

    .error {
        padding: 0.25rem;
        color: red;
    }
</style>
